export const brand = {
  name: '50 Mapas Visuales de Electroterapia',
  promise: '+50 Mapas Visuales de Posicionamiento de Electrodos',
  promiseAccent: 'para Kinesiología y Fisioterapia',
  subtitle:
    'Consulta en segundos dónde colocar los electrodos para TENS, NMES, FES e interferencial. Cada mapa integra anatomía, posicionamiento, electrodos, pares y canales en una sola referencia visual.',
  mockupCaption: '50 Mapas Visuales de Posicionamiento de Electrodos + 4 Bonos Profesionales',
}

export const heroBadges: string[] = ['Acceso inmediato', 'Imprimible', 'Tuyo de por vida']

export interface Stat {
  value: string
  label: string
}

export const stats: Stat[] = [
  { value: '50', label: 'mapas visuales' },
  { value: '4', label: 'modalidades principales' },
  { value: '100', label: 'referencias de puntos motores' },
  { value: '4', label: 'bonos profesionales' },
]

export const proofNote =
  'Un sistema visual de consulta para estudiantes y profesionales de Kinesiología y Fisioterapia'

export const universities: string[] = ['TENS', 'NMES', 'FES', 'Interferencial']

export interface PainGroup {
  audience: string
  points: string[]
}

export const painGroups: PainGroup[] = [
  {
    audience: 'Si eres estudiante de Kinesiología o Fisioterapia',
    points: [
      'Estudias electroterapia, pero después dudas dónde colocar los electrodos',
      'Entiendes TENS, NMES o FES en teoría, pero te cuesta visualizar la aplicación',
      'Tienes apuntes, capturas y PDFs dispersos y pierdes tiempo buscando lo que necesitas',
      'Confundes electrodos, pares o canales cuando aparecen configuraciones más complejas',
    ],
  },
  {
    audience: 'Si ya eres kinesiólogo/a o fisioterapeuta',
    points: [
      'Quieres comprobar rápidamente una colocación antes de una aplicación',
      'No quieres depender únicamente de la memoria para recordar cada región',
      'Necesitas tener tus referencias de electroterapia organizadas en un solo lugar',
      'Quieres consultar desde el celular, tablet o tener los mapas impresos en tu espacio de trabajo',
    ],
  },
]

export const solutionPoints: string[] = [
  '50 mapas visuales: encuentra rápidamente la región que necesitas.',
  'Posicionamiento ilustrado: visualiza los electrodos directamente sobre la anatomía.',
  'Electrodos y canales identificados: E1, E2, E3, E4, CH1 y CH2 cuando corresponde.',
  'Organizado por modalidad: TENS, NMES, FES e interferencial.',
  'Referencias anatómicas claras: identifica fácilmente cada región y grupo muscular.',
  '4 bonos profesionales: parámetros, seguridad, puntos motores y fichas rápidas.',
]

export interface Module {
  number: string
  title: string
  count: string
  detail: string
  summary: string
  closing: string
  spine: string
}

export const modules: Module[] = [
  {
    number: '01',
    title: 'TENS',
    count: '15',
    detail: '15 mapas visuales',
    summary:
      'Región cervical, cervicotorácica, trapecio, escápula, hombro, lumbar, sacroilíaca, codo, muñeca, mano, cadera, rodilla, región posterior de rodilla, tobillo y pie.',
    closing: 'Posicionamiento regional para consulta visual rápida.',
    spine: 'bg-blue-400',
  },
  {
    number: '02',
    title: 'NMES',
    count: '18',
    detail: '18 mapas visuales',
    summary:
      'Cuádriceps, vasto medial, isquiotibiales, glúteo mayor, glúteo medio, tibial anterior, gastrocnemio, sóleo, fibulares, deltoides, bíceps, tríceps, flexores y extensores.',
    closing: 'Referencias musculares y distribución de electrodos claramente identificadas.',
    spine: 'bg-blue-600',
  },
  {
    number: '03',
    title: 'FES + Aplicaciones Funcionales',
    count: '9',
    detail: '9 mapas visuales',
    summary:
      'Dorsiflexión, pie caído, extensión y flexión de rodilla, extensión de cadera, abducción de hombro, flexión y extensión de codo y extensión de muñeca.',
    closing: 'Relaciona la estimulación con la respuesta funcional buscada.',
    spine: 'bg-blue-700',
  },
  {
    number: '04',
    title: 'Interferencial + Técnicas de Colocación',
    count: '8',
    detail: '8 mapas visuales',
    summary:
      'Configuración bipolar/premodulada, tetrapolar, lumbar, rodilla, hombro, colocación bipolar longitudinal, cuadripolar y punto motor vs. colocación convencional.',
    closing: 'Visualiza canales, pares y lógica de distribución.',
    spine: 'bg-blue-900',
  },
]

export const mapIncludes: string[] = [
  'Corriente o modalidad utilizada',
  'Región anatómica',
  'Grupo muscular u objetivo',
  'Electrodos identificados',
  'Canales y pares',
  'Posicionamiento visual',
  'Respuesta buscada',
  'Puntos de atención y precauciones',
]

export interface GalleryItem {
  title: string
  caption: string
  image: string
}

export const gallery: GalleryItem[] = [
  {
    title: 'TENS — Región cervical posterior',
    caption:
      'Paravertebrales cervicales, trapecio superior y suboccipitales: 4 electrodos en 2 canales, con respuesta esperada y precauciones.',
    image: 'https://cdn.magicpatterns.com/uploads/jkFFQYPHQ6qrDNVATE6ueT/1.webp',
  },
  {
    title: 'TENS — Hombro',
    caption:
      'Deltoides, manguito rotador, trapecio superior e infraespinoso: colocación alrededor de la región, canales y pares identificados.',
    image: 'https://cdn.magicpatterns.com/uploads/4XKqajqDKMPzAdK5NYDA1n/2.webp',
  },
  {
    title: 'TENS — Región lumbar',
    caption:
      'Erectores espinales, multífidos y cuadrado lumbar: electrodos paralelos a la columna, de L2 a L5, con precauciones clave.',
    image: 'https://cdn.magicpatterns.com/uploads/vfmu1YasXEPju15pQ7gFwU/3.webp',
  },
  {
    title: 'FES / NMES — Abducción de hombro',
    caption:
      'Deltoides en porción media: vista anterior y lateral, 2 electrodos alineados con las fibras y la respuesta funcional buscada.',
    image: 'https://cdn.magicpatterns.com/uploads/sRTieJinxd5RAVPy68ReBa/4.webp',
  },
  {
    title: 'TENS — Muñeca',
    caption:
      'Carpo y estructuras periarticulares: vista dorsal y palmar, 4 electrodos sobre extensores y flexores en 2 canales.',
    image:
      'https://cdn.magicpatterns.com/uploads/sZ9XJFGaPq22RCpz1FuNiW/Captura_de_pantalla_2026-09-23_a_la(s)_2.11.31_a._m..png',
  },
  {
    title: 'TENS — Tobillo',
    caption:
      'Región periarticular del tobillo: vista anterior y lateral, 4 electrodos alrededor de los maléolos en 2 canales.',
    image:
      'https://cdn.magicpatterns.com/uploads/uiB2K8KdzVVyfUdwiMwABj/Captura_de_pantalla_2026-09-23_a_la(s)_2.12.28_a._m..png',
  },
]

export interface Bonus {
  tag: string
  title: string
  subtitle: string
  value: string
  benefit: string
  detail: string
  image: string
}

export const bonusesTotalValue = 'USD 20,60'

export const bonuses: Bonus[] = [
  {
    tag: 'Bono 01',
    title: 'Guía Rápida de Parámetros de Electroterapia',
    subtitle: 'TENS • NMES • FES • INTERFERENCIAL',
    value: 'USD 4,90',
    benefit:
      'Frecuencia, ancho de pulso, intensidad, rampas, ciclos de trabajo y tiempos explicados mediante tablas y referencias visuales.',
    detail: '30 páginas ilustradas y prácticas',
    image: 'https://cdn.magicpatterns.com/uploads/hohfqFgzopFHrVdRnYDerU/mockupbono1.webp',
  },
  {
    tag: 'Bono 02',
    title: 'Checklist de Seguridad y Contraindicaciones',
    subtitle: 'Antes • Durante • Después',
    value: 'USD 4,90',
    benefit:
      'Una guía visual para revisar piel, sensibilidad, dispositivos implantados, electrodos, zonas de precaución, respuesta del paciente y señales para detener y reevaluar.',
    detail: '30 páginas + checklist imprimible',
    image: 'https://cdn.magicpatterns.com/uploads/iaeTQYmqWmLTqemLk6rdtu/Mockupbono2.webp',
  },
  {
    tag: 'Bono 03',
    title: 'Atlas Visual de Puntos Motores',
    subtitle: 'Localización rápida por grupo muscular',
    value: 'USD 6,90',
    benefit:
      'Bíceps, tríceps, deltoides, cuádriceps, isquiotibiales, tibial anterior, gastrocnemio, glúteos, flexores, extensores y más.',
    detail: '100 ilustraciones anatómicas con referencias visuales de búsqueda',
    image: 'https://cdn.magicpatterns.com/uploads/w6XvGqt8dCPX7cBVWnSBFD/mockupbono3.webp',
  },
  {
    tag: 'Bono 04',
    title: 'Fichas de Consulta Rápida por Corriente',
    subtitle: 'TENS • NMES • FES • INTERFERENCIAL',
    value: 'USD 3,90',
    benefit:
      '4 fichas visuales que resumen objetivo, parámetros principales, respuesta buscada, colocaciones habituales y precauciones.',
    detail: 'Una corriente por ficha. Información clave en segundos.',
    image: 'https://cdn.magicpatterns.com/uploads/1U2cSBprdv6t4HFNqSracJ/mockupbono4.webp',
  },
]

export interface Testimonial {
  initials: string
  quote: string
  author: string
  role: string
  city: string
}

export const testimonials: Testimonial[] = [
  {
    initials: 'LM',
    quote:
      'Electroterapia siempre me costaba porque tenía todo separado entre apuntes. Con los mapas puedo ver la región, los electrodos y los canales juntos y lo entiendo mucho más rápido.',
    author: 'Laura M.',
    role: 'Estudiante de Fisioterapia',
    city: 'Bogotá, Colombia',
  },
  {
    initials: 'DC',
    quote:
      'Lo tengo en la tablet y me sirve como referencia rápida. Busco la región y en segundos tengo el esquema visual frente a mí.',
    author: 'Diego C.',
    role: 'Fisioterapeuta',
    city: 'Ciudad de México, México',
  },
  {
    initials: 'VS',
    quote:
      'Lo que más me ayudó fue poder visualizar los electrodos directamente sobre la anatomía. Para estudiar NMES y FES me resultó mucho más claro.',
    author: 'Valentina S.',
    role: 'Estudiante de Kinesiología',
    city: 'Santiago, Chile',
  },
]

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: '¿Cuándo lo recibo?',
    answer:
      'El acceso es inmediato después de confirmar el pago. Recibes el material digital para comenzar a consultarlo desde tu dispositivo.',
  },
  {
    question: '¿Sirve si soy estudiante de Kinesiología o Fisioterapia?',
    answer:
      'Sí. El material está diseñado para facilitar la comprensión visual del posicionamiento de electrodos y complementar el estudio de electroterapia. No reemplaza tus clases, bibliografía ni formación profesional.',
  },
  {
    question: '¿Sirve si ya soy kinesiólogo/a o fisioterapeuta?',
    answer:
      'Sí. Además de servir como material de estudio, está organizado para funcionar como herramienta de consulta rápida. Puedes buscar la región o modalidad que necesitas y visualizar la referencia correspondiente.',
  },
  {
    question: '¿Qué modalidades incluye?',
    answer:
      'El sistema trabaja principalmente con TENS, NMES, FES e interferencial. También incluye mapas específicos sobre configuraciones bipolares, cuadripolares y puntos motores.',
  },
  {
    question: '¿Puedo verlo desde el celular?',
    answer: 'Sí. Al ser digital, puedes consultarlo desde celular, tablet o computadora.',
  },
  {
    question: '¿Se pueden imprimir los mapas?',
    answer: 'Sí. Los archivos están preparados en alta calidad y pueden imprimirse para uso personal.',
  },
  {
    question: '¿En qué paquete están incluidos los bonos?',
    answer:
      'Los 4 bonos están incluidos con el Sistema Completo: Guía de Parámetros, Checklist de Seguridad, Atlas de Puntos Motores y Fichas de Consulta por Corriente.',
  },
  {
    question: '¿Me dice exactamente qué parámetros utilizar con cada paciente?',
    answer:
      'No. La guía ofrece referencias educativas para comprender los parámetros y facilitar su consulta. La selección final debe individualizarse según evaluación, objetivo, paciente, equipo utilizado y criterio profesional.',
  },
  {
    question: '¿El material reemplaza la evaluación profesional?',
    answer:
      'No. Los mapas son herramientas educativas y de consulta visual. No reemplazan la evaluación clínica, la formación profesional, las instrucciones del fabricante ni los protocolos institucionales.',
  },
  {
    question: '¿El acceso es de por vida?',
    answer: 'Sí. Realizas un único pago y el material queda disponible para ti.',
  },
  {
    question: '¿Es adecuado para utilizar como material de consulta?',
    answer:
      'Sí. El sistema fue organizado precisamente para facilitar la búsqueda visual por región, músculo o modalidad. Siempre debe utilizarse dentro del alcance de la formación y criterio profesional correspondiente.',
  },
]

export const scarcity = {
  total: 50,
  left: 42,
  note: 'Precio especial para los primeros 50.',
  nextPrice: 'USD 14,90',
}

export const checkoutUrl = 'https://electrodosfisio.impultienda.ar'

export interface Offer {
  name: string
  summary: string
  finalSummary: string
  includes: string[]
  listPrice: string
  totalValue: string
  price: string
  discount: string
  savings: string
  cta: string
  finalCta: string
}

export const offer: Offer = {
  name: '50 Mapas Visuales de Electroterapia — Sistema Completo',
  summary: '50 mapas + Atlas de 100 puntos motores + 3 herramientas profesionales',
  finalSummary: '50 mapas visuales + 4 bonos profesionales',
  includes: [
    '50 Mapas Visuales de Posicionamiento de Electrodos',
    'TENS, NMES, FES e interferencial',
    'Miembros superiores e inferiores',
    'Columna, tronco, hombro y cadera',
    'Electrodos, pares y canales identificados',
    'Aplicaciones funcionales',
    'Guía Rápida de Parámetros',
    'Checklist de Seguridad y Contraindicaciones',
    'Atlas Visual de Puntos Motores',
    'Fichas de Consulta Rápida por Corriente',
    'PDF en alta calidad',
    'Imprimible',
    'Acceso de por vida',
  ],
  listPrice: 'USD 19,90',
  totalValue: 'USD 40,50',
  price: 'USD 9,90',
  discount: '50% off',
  savings: 'USD 10,00',
  cta: 'Quiero los 50 Mapas + 4 Bonos',
  finalCta: 'Quiero los 50 Mapas ahora',
}

export const pricingRows: { label: string; value: string }[] = [
  { label: '50 Mapas Visuales de Posicionamiento de Electrodos (PDF imprimible)', value: 'USD 19,90' },
  { label: '4 bonos profesionales incluidos', value: 'USD 20,60' },
]

export const heroImage = 'https://cdn.magicpatterns.com/uploads/fS1ke73M6GWLwnzFbFDLbo/mockp2.webp'

export interface PurchaseNotice {
  name: string
  city: string
  product: string
}

export const purchaseNotices: PurchaseNotice[] = [
  { name: 'Camila R.', city: 'Lima', product: '50 Mapas Visuales + 4 bonos' },
  { name: 'Andrés B.', city: 'Medellín', product: '50 Mapas Visuales + 4 bonos' },
  { name: 'Sofía D.', city: 'Guadalajara', product: '50 Mapas Visuales + 4 bonos' },
]

export const footerLinks: { label: string; href: string }[] = [
  { label: 'Términos de Servicio', href: '#terminos' },
  { label: 'Política de Privacidad', href: '#privacidad' },
  { label: 'Contacto', href: '#contacto' },
]
